import { Button, Modal, Typography } from 'components';
import { Member } from 'gql/graphql';
import { toaster } from 'helpers/toaster';
import { CloseIcon, ErrorModalFeaturedIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { useMemberRemove } from 'providers/UserProvider/useMemberRemove';
import { FC, useCallback, useRef } from 'react';
import { notifyException } from 'services/appsignal';
import { ESpansException } from 'types/ESpans';
import { cn } from 'utils/cn';
import * as yup from 'yup';
import YupPassword from 'yup-password';

YupPassword(yup);

interface IModalDeleteMemberAccount {
  open: boolean;
  member: Member | null;
  onCancel: () => void;
}

const ModalDeleteMemberAccount: FC<IModalDeleteMemberAccount> = ({ open, member, onCancel }) => {
  const t = useTranslations();

  const refModal = useRef<HTMLDivElement>(null);

  const { mutateAsync: removeMember } = useMemberRemove({
    onSuccess: () => toaster.success(t('messages.success.delete_member')),
    onError: (error: unknown) => {
      notifyException(error, ESpansException.AccountDetailsException);
      toaster.error(t('messages.error.delete_member'));
    }
  });

  const handleDelete = useCallback(async () => {
    if (member?.id) {
      removeMember({
        memberUserId: member?.id
      });
      onCancel();
    }
  }, [member?.id, onCancel, removeMember]);

  return (
    <Modal
      zDialog={cn('!z-[1999]')}
      zDialogBackdrop="z-[1000]"
      onClose={onCancel}
      open={open}
      className={'max-h-[60vh] w-[544px]'}
    >
      <div className="absolute right-2 top-2 z-[1000]">
        <Button
          size="small"
          variant="icon"
          color="neutral"
          className="!w-8 rounded-full"
          onClick={onCancel}
          startAdornment={<CloseIcon />}
        />
      </div>
      <div
        className={cn(
          'overflow-y-auto px-8 py-6',
          'relative flex w-full flex-col justify-center  gap-2 rounded bg-white shadow-custom'
        )}
        ref={refModal}
      >
        <ErrorModalFeaturedIcon />
        <Typography variant="medium4xl" className=" text-tertiary-100 ">
          {t('account_details.deleteMember.title')}
        </Typography>
        <Typography variant="light4" className="mt-2 flex">
          <div
            className="max-w-xl text-justify"
            dangerouslySetInnerHTML={{
              __html: t
                .raw('account_details.deleteMember.paragraph')
                .replace('{email}', member?.email ?? '')
            }}
          />
        </Typography>
        <div className="mt-4 flex justify-end gap-4">
          <Button
            size="medium"
            label={t('account_details.cancel')}
            onClick={onCancel}
            className="flex items-center"
            variant="text"
          />
          <Button
            size="medium"
            label={t('account_details.deleteMember.button')}
            onClick={handleDelete}
            color="danger"
            className="flex items-center"
            variant="contained"
          />
        </div>
      </div>
    </Modal>
  );
};

export default ModalDeleteMemberAccount;
